import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { useEffect } from 'react'
import { useElementItemStore } from '../ElementItemStore'
import { useShallowSelector, useWorkspace2Actions } from '../Workspace2Store'
import { useProductFetcher } from '../api/useProductFetcher/useProductFetcher'
import type { DataNode, WorkspaceEditorProps } from '../types/types'
import { getActiveTabLabel } from '../utils'
import { usePermissionChecks } from './usePermissionChecks'

/** Props for the useContentItemFetcher hook. */
export interface UseEditorControllerProps extends WorkspaceEditorProps {
    /** The current tree data structure */
    treeData: DataNode[]
    /** Function the updates the tree data structure */
    setTreeData: (treeData: DataNode[]) => void
    /** Conditional flags use for task permission */
    permission: Partial<ReturnType<typeof usePermissionChecks>>
}

/**
 * Hook that create memoized editor Tab configuration.
 * Does a side-effect that calculates when a refetch is needed to update the Tree structure.,
 * and sets current active tab if necessary.
 * @param {UseEditorControllerProps} props - The props for the hook.
 * @returns {object} The memoized editor tab configuration and product family fetcher function.
 */
export const useContentItemFetcher = ({
    projectId,
    treeData,
    setTreeData,
    permission,
}: UseEditorControllerProps) => {
    const { isProductTypeProject } = permission
    const { setActiveTab } = useWorkspace2Actions()
    const isBPAutomationInProgress = useShallowSelector(
        (state) => state.isBPAutomationInProgress
    )
    const elementItemValue = useElementItemStore((s) => s.elementItemValue)
    const { elementId, elementType, actionFlag } = useElementItemStore(
        (s) => s.elementItemValue
    )
    const setElementItemValue = useElementItemStore(
        (s) => s.setElementItemValue
    )

    // Custom hook that determines whether a fetch function to get Content Items and DTV details of selected node.
    const {
        fetchFamilyContentItemView,
        fetchProductTypeContentItemView,
    } = useProductFetcher({
        projectId,
        overrideContentItemNodes: !treeData?.length,
    })
    const fetchContentItemView = isProductTypeProject
        ? fetchProductTypeContentItemView
        : fetchFamilyContentItemView

    /**
     * When selected element node changes, triggers side-effects to:
     * - reset tree data if needed and fetch product family
     * - set active tab if needed
     */
    useEffect(() => {
        if (
            elementId &&
            (actionFlag === ProjectElementType.PRODUCTFAMILY ||
                actionFlag === ProjectElementType.PRODUCTTYPE)
        ) {
            // Always reset to Part 1 on family selection; product type selection will always start on Part 2.
            const latestActiveTab = isProductTypeProject ? 2 : 1
            setActiveTab(latestActiveTab)
            setTreeData([])

            // Skip content fetch if BP automation is in progress
            // Content will be fetched after BP completes via handleBPComplete callback
            if (fetchContentItemView && !isBPAutomationInProgress) {
                fetchContentItemView(getActiveTabLabel(latestActiveTab))
            }
        }

        // Prevent unnecessary state updates to avoid re-render loop
        if (actionFlag !== '') {
            setElementItemValue({
                ...elementItemValue,
                actionFlag: '',
            })
        }
    }, [
        actionFlag,
        isProductTypeProject,
        elementItemValue,
        isBPAutomationInProgress,
        setElementItemValue,
    ])

    return {
        fetchContentItemView,
    }
}
